/**
 * DragOverlayCanvas — logo / QR 拖拽 + 缩放 + 旋转 编辑器。
 *
 * 2026-06-23 从 features/poster/ui/components/overlay 抽到 shared/ui:海报详情页 + 视频「成片编辑」
 * 面板共用(FSD:features 互不 import,跨 feature UI 提到 shared)。
 *
 * 共用场景:
 * - admin 模板编辑页 — 设模板默认位置(写 schema_json.defaultOverlay)
 * - 海报成品编辑页 — 微调单张 asset 位置(写 asset.overlay_config_json)
 * - 视频成片编辑面板 — 在封面帧上拖 logo / 二维码(写 video task.overlayConfig)
 *
 * 核心约束:
 * - 纯 UI 组件 — 无 API 调用,无副作用;接 value + onChange
 * - 归一化坐标 0..1 — 跟图片像素尺寸解耦,不同生成尺寸可复用同一 overlay
 * - readOnly 模式仅展示不交互,用于详情页预览
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Rnd, type RndResizeCallback, type RndDragCallback } from "react-rnd";
import type { OverlayConfig, OverlayItem } from "@/shared/lib/overlay/overlayConfig";
import { pxRectToBbox } from "@/shared/lib/overlay/bbox";
import { anchorYToPx, pxToAnchorY, pxToWidthPct } from "@/shared/lib/overlay/subtitleBand";
import { cn } from "@/shared/lib/utils";

const SAMPLE_LOGO = "/assets/sample-logo.png";
const SAMPLE_QR = "/assets/sample-qr.png";

/**
 * 4 角小圆点 — 给用户一个「这玩意儿可以拖」的视觉钩子。
 * pointer-events-none 不抢 Rnd 自己的 drag handle。
 */
function OverlayHandle({ pos }: { pos: "tl" | "tr" | "bl" | "br" }) {
  const positions = {
    tl: "-top-1 -left-1",
    tr: "-top-1 -right-1",
    bl: "-bottom-1 -left-1",
    br: "-bottom-1 -right-1",
  };
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute h-2 w-2 rounded-full border border-primary bg-white shadow-sm",
        positions[pos]
      )}
    />
  );
}

/**
 * VID-D(2026-08-18):字幕带 —— 与 logo/二维码同一块画布上所见即所得。
 * ★只给**竖向**拖 + 宽度手柄:字幕居中最稳,放开左右会拖出偏心成片(老板认过这个取舍)。
 */
export interface SubtitleBand {
  /** 竖向锚点 0..1(字幕行中心) */
  anchorY: number;
  /** 宽度占比 0.3..1(决定多长换行) */
  widthPct: number;
  /** 预览文案(取本片第一句真口播;没有则不渲染整条带) */
  sampleText: string;
  fontPx: number;
  outlinePx: number;
}

export interface DragOverlayCanvasProps {
  baseImageUrl: string;
  brandAssets: {
    logoUrl?: string;
    qrUrl?: string;
    /** 用占位 logo(admin 模板编辑场景,租户 brand-asset 未绑定) */
    fallbackLogo?: boolean;
    /** 用占位 QR */
    fallbackQr?: boolean;
  };
  value: OverlayConfig | null;
  onChange: (next: OverlayConfig) => void;
  readOnly?: boolean;
  className?: string;
  /**
   * V27.5 (2026-05-14): admin 模板编辑模式 — 框内不渲染实图,
   * 改成虚线框 + 中央文字标签(「Logo 位置」/「二维码 位置」)。
   * 因为 admin 设位置时还没绑定具体租户 logo/QR,塞 sample 图反而误导。
   */
  placeholderLabels?: boolean;
  /** VID-D:字幕带(缺省不渲染 —— 海报/admin 模板编辑场景没有字幕)。 */
  subtitle?: SubtitleBand | null;
  /** 拖动/改宽落定回调(归一化值);缺省=只读预览不可拖。 */
  onSubtitleChange?: (next: { anchorY: number; widthPct: number }) => void;
}

/**
 * V6 fix4 (2026-05-12): bbox → 像素矩形。
 *
 * `imgAspect` (= 自然 height / 自然 width) 决定 height,**忽略 bbox.h** — 跟后端
 * `_paste_logo` 一致(后端按 `target_h = logo.height * (target_w / logo.width)` 算)。
 *
 * 老逻辑直接乘 canvasH 在竖版海报上把方 logo 拉成竖长方形,跟实物渲染不一致。
 *
 * 没传 imgAspect 时回退到 bbox.h(过渡兼容,组件第一次渲染图还没 load)。
 */
function bboxToPxRect(
  item: OverlayItem | null,
  canvasW: number,
  canvasH: number,
  imgAspect: number | null
) {
  if (!item) return null;
  const widthPx = Math.round(item.w * canvasW);
  const heightPx =
    imgAspect != null && imgAspect > 0
      ? Math.round(widthPx * imgAspect)
      : Math.round(item.h * canvasH);
  return {
    x: Math.round(item.x * canvasW),
    y: Math.round(item.y * canvasH),
    width: widthPx,
    height: heightPx,
  };
}

export function DragOverlayCanvas({
  baseImageUrl,
  brandAssets,
  value,
  onChange,
  readOnly = false,
  className,
  placeholderLabels = false,
  subtitle = null,
  onSubtitleChange,
}: DragOverlayCanvasProps) {
  const { t } = useTranslation("poster");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  // V6 fix4 (2026-05-12): 测出 logo / QR 自然 aspect (= naturalH / naturalW),
  // 用来决定拖拽框的实际形状 + react-rnd lockAspectRatio,跟后端 _paste_logo 一致。
  const [logoAspect, setLogoAspect] = useState<number | null>(null);
  const [qrAspect, setQrAspect] = useState<number | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      setSize({ w: rect.width, h: rect.height });
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const cfg: OverlayConfig = value ?? { version: 1, logo: null, qr: null };

  const logoUrl = brandAssets.logoUrl ?? (brandAssets.fallbackLogo ? SAMPLE_LOGO : null);
  const qrUrl = brandAssets.qrUrl ?? (brandAssets.fallbackQr ? SAMPLE_QR : null);

  // 图片 url 变了重测 aspect
  useEffect(() => {
    setLogoAspect(null);
  }, [logoUrl]);
  useEffect(() => {
    setQrAspect(null);
  }, [qrUrl]);

  const update = (kind: "logo" | "qr", next: OverlayItem) => {
    onChange({ ...cfg, [kind]: next });
  };

  const logoRect = bboxToPxRect(cfg.logo, size.w, size.h, logoAspect);
  const qrRect = bboxToPxRect(cfg.qr, size.w, size.h, qrAspect);

  return (
    <div
      ref={containerRef}
      data-testid="overlay-canvas-root"
      className={cn(
        "relative overflow-hidden rounded-md border border-border/60 bg-background/40",
        className
      )}
    >
      <img
        src={baseImageUrl}
        alt={t("canvas.baseImageAlt")}
        loading="lazy"
        decoding="async"
        className="block w-full select-none"
        draggable={false}
      />

      {cfg.logo && logoRect && size.w > 0 && (placeholderLabels || logoUrl) && (
        <Rnd
          bounds="parent"
          position={{ x: logoRect.x, y: logoRect.y }}
          size={{ width: logoRect.width, height: logoRect.height }}
          disableDragging={readOnly}
          enableResizing={
            readOnly
              ? false
              : { topLeft: true, topRight: true, bottomLeft: true, bottomRight: true }
          }
          /* V6 fix4: 用 logo 自然 aspect 锁框形,跟后端合成保持一致;
             V27.5 admin placeholder 模式没绑实图,不锁 aspect,自由拖。 */
          lockAspectRatio={
            placeholderLabels
              ? false
              : logoAspect != null && logoAspect > 0
                ? 1 / logoAspect
                : false
          }
          onDragStop={
            ((_, d) => {
              const next = pxRectToBbox(
                { x: d.x, y: d.y, width: logoRect.width, height: logoRect.height },
                size.w,
                size.h,
                cfg.logo
              );
              update("logo", next);
            }) as RndDragCallback
          }
          onResizeStop={
            ((_, _dir, ref, _delta, pos) => {
              const next = pxRectToBbox(
                {
                  x: pos.x,
                  y: pos.y,
                  width: ref.offsetWidth,
                  height: ref.offsetHeight,
                },
                size.w,
                size.h,
                cfg.logo
              );
              update("logo", next);
            }) as RndResizeCallback
          }
        >
          <div
            data-overlay-kind="logo"
            data-readonly={readOnly}
            className={cn(
              "group relative h-full w-full",
              !readOnly &&
                "cursor-move outline-dashed outline-1 outline-primary/60 hover:outline-2 hover:outline-primary",
              placeholderLabels && "outline-2 outline-dashed bg-primary/5"
            )}
            style={{
              transform:
                !placeholderLabels && cfg.logo.rotation
                  ? `rotate(${cfg.logo.rotation}deg)`
                  : undefined,
              transformOrigin: "center center",
            }}
          >
            {placeholderLabels ? (
              <div className="pointer-events-none flex h-full w-full items-center justify-center text-center text-2xs font-semibold leading-tight text-primary/80">
                {t("canvas.logoPlaceholder")}
              </div>
            ) : (
              logoUrl && (
                <img
                  src={logoUrl}
                  alt="logo overlay"
                  draggable={false}
                  className="pointer-events-none h-full w-full object-contain"
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                      setLogoAspect(img.naturalHeight / img.naturalWidth);
                    }
                  }}
                />
              )
            )}
            {!readOnly && (
              <>
                <OverlayHandle pos="tl" />
                <OverlayHandle pos="tr" />
                <OverlayHandle pos="bl" />
                <OverlayHandle pos="br" />
              </>
            )}
          </div>
        </Rnd>
      )}

      {cfg.qr && qrRect && size.w > 0 && (placeholderLabels || qrUrl) && (
        <Rnd
          bounds="parent"
          position={{ x: qrRect.x, y: qrRect.y }}
          size={{ width: qrRect.width, height: qrRect.height }}
          disableDragging={readOnly}
          enableResizing={
            readOnly
              ? false
              : { topLeft: true, topRight: true, bottomLeft: true, bottomRight: true }
          }
          /* V6 fix4: QR 用自然 aspect (实测一般 1:1,极少异形);测不到 fallback 锁 1:1;
             V27.5 admin placeholder 模式锁 1:1(二维码必方) */
          lockAspectRatio={
            placeholderLabels ? true : qrAspect != null && qrAspect > 0 ? 1 / qrAspect : true
          }
          onDragStop={
            ((_, d) => {
              const next = pxRectToBbox(
                { x: d.x, y: d.y, width: qrRect.width, height: qrRect.height },
                size.w,
                size.h,
                cfg.qr
              );
              update("qr", next);
            }) as RndDragCallback
          }
          onResizeStop={
            ((_, _dir, ref, _delta, pos) => {
              const next = pxRectToBbox(
                {
                  x: pos.x,
                  y: pos.y,
                  width: ref.offsetWidth,
                  height: ref.offsetHeight,
                },
                size.w,
                size.h,
                cfg.qr
              );
              update("qr", next);
            }) as RndResizeCallback
          }
        >
          <div
            data-overlay-kind="qr"
            data-readonly={readOnly}
            className={cn(
              "group relative h-full w-full",
              !readOnly &&
                "cursor-move outline-dashed outline-1 outline-primary/60 hover:outline-2 hover:outline-primary",
              placeholderLabels && "outline-2 outline-dashed bg-primary/5"
            )}
          >
            {placeholderLabels ? (
              <div className="pointer-events-none flex h-full w-full items-center justify-center text-center text-2xs font-semibold leading-tight text-primary/80">
                {t("canvas.qrPlaceholder")}
              </div>
            ) : (
              qrUrl && (
                <img
                  src={qrUrl}
                  alt="QR overlay"
                  draggable={false}
                  className="pointer-events-none h-full w-full object-contain"
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                      setQrAspect(img.naturalHeight / img.naturalWidth);
                    }
                  }}
                />
              )
            )}
            {!readOnly && (
              <>
                <OverlayHandle pos="tl" />
                <OverlayHandle pos="tr" />
                <OverlayHandle pos="bl" />
                <OverlayHandle pos="br" />
              </>
            )}
          </div>
        </Rnd>
      )}

      {/* ── VID-D 字幕带:竖向拖 + 左右宽度手柄(横向位置不给动,恒居中)── */}
      {subtitle && size.w > 0 && subtitle.sampleText && (
        <Rnd
          bounds="parent"
          dragAxis="y"
          position={{
            x: Math.round(((1 - subtitle.widthPct) / 2) * size.w),
            y: anchorYToPx(subtitle.anchorY, subtitle.fontPx * 1.5, size.h),
          }}
          size={{
            width: Math.round(subtitle.widthPct * size.w),
            height: Math.round(subtitle.fontPx * 1.5),
          }}
          disableDragging={readOnly || !onSubtitleChange}
          enableResizing={
            readOnly || !onSubtitleChange ? false : { left: true, right: true }
          }
          onDragStop={
            ((_, d) => {
              if (!onSubtitleChange) return;
              onSubtitleChange({
                anchorY: pxToAnchorY(d.y, subtitle.fontPx * 1.5, size.h),
                widthPct: subtitle.widthPct,
              });
            }) as RndDragCallback
          }
          onResizeStop={
            ((_, _dir, ref) => {
              if (!onSubtitleChange) return;
              // 只取宽度:改宽后仍居中(左右手柄任拖一边,结果都是对称收窄)
              onSubtitleChange({
                anchorY: subtitle.anchorY,
                widthPct: pxToWidthPct(ref.offsetWidth, size.w),
              });
            }) as RndResizeCallback
          }
        >
          <div
            data-overlay-kind="subtitle"
            className={cn(
              "flex h-full w-full items-center justify-center overflow-hidden rounded-sm px-1 text-center",
              !readOnly && onSubtitleChange && "cursor-ns-resize outline-dashed outline-1 outline-white/50"
            )}
          >
            <span
              className="truncate font-semibold text-white"
              style={{
                fontSize: `${subtitle.fontPx}px`,
                lineHeight: 1.2,
                // 四向阴影模拟 ASS 描边;none 档 outlinePx=0 → 无描边(亮背景会糊,与成片一致)
                textShadow: subtitle.outlinePx
                  ? [
                      `-${subtitle.outlinePx}px -${subtitle.outlinePx}px 0 #000`,
                      `${subtitle.outlinePx}px -${subtitle.outlinePx}px 0 #000`,
                      `-${subtitle.outlinePx}px ${subtitle.outlinePx}px 0 #000`,
                      `${subtitle.outlinePx}px ${subtitle.outlinePx}px 0 #000`,
                    ].join(", ")
                  : "none",
              }}
            >
              {subtitle.sampleText}
            </span>
          </div>
        </Rnd>
      )}
    </div>
  );
}
